"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  TextField,
  SelectField,
  TextareaField,
  SwitchField,
  S3UploadField,
  MultiSelectField,
} from "@admin/_components/form-fields";
import { useApiPost } from "@admin/_lib/api-hooks";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "next-auth/react";

// ── Schema ────────────────────────────────────────────────────────────────────

const schema = z
  .object({
    name: z.string().min(1, "Package name is required"),
    priority: z.string().min(1, "Priority is required"),
    purchase_type: z.string().min(1, "Purchase type is required"),
    package_type: z.string().optional().default(""),
    // PM-02: price is required (Angular parity)
    price: z.string().min(1, "Price is required"),
    sessions: z.string().optional().default(""),
    subscription_period: z.string().optional().default(""),
    // PM-02: description is required (Angular parity)
    description: z.string().min(1, "Description is required"),
    // PM-02: webinar multi-select
    webinar_ids: z.array(z.string()).optional().default([]),
    // PM-02: package image (S3 metadata array)
    image: z.array(z.any()).optional().default([]),
    status: z.boolean().default(true),
  })
  // PM-03: purchase-type-specific branch validation
  .superRefine((data, ctx) => {
    if (data.purchase_type === "multiple" && !data.sessions?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Number of sessions is required for multiple purchase type",
        path: ["sessions"],
      });
    }
    if (data.purchase_type === "subscription" && !data.subscription_period) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Subscription period is required for subscription purchase type",
        path: ["subscription_period"],
      });
    }
  });

type FormValues = z.infer<typeof schema>;

// ── Package Form ──────────────────────────────────────────────────────────────

export function PackageForm({ id }: { id?: string }) {
  const router = useRouter();
  const isEdit = !!id;
  const { data: session } = useSession();

  const baseUrl =
    process.env.NEXT_PUBLIC_API_CLOUDFRONT ?? "https://api.divineinfinitebeing.com/";

  // ── Fetch existing record (edit mode) ─────────────────────────────────────

  const { data: record, isLoading: isFetching } = useQuery({
    queryKey: ["package/fetch-single-package", id],
    queryFn: async () => {
      const res = await fetch(`${baseUrl}package/fetch-single-package`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.token}`,
        },
        body: JSON.stringify({ _id: id }),
      });
      const data = await res.json();
      return data.results?.res ?? data.results ?? data;
    },
    enabled: isEdit && !!session?.token,
  });

  // ── Fetch all webinars for multi-select ───────────────────────────────────

  const { data: webinarList } = useQuery({
    queryKey: ["webinar/fetch-all-webinar"],
    queryFn: async () => {
      const res = await fetch(`${baseUrl}webinar/fetch-all-webinar`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.token}`,
        },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      return (data.results?.res ?? data.results ?? data ?? []) as Record<string, unknown>[];
    },
    enabled: !!session?.token,
  });

  const webinarOptions = (webinarList ?? []).map((w) => ({
    value: String(w._id ?? w.id ?? ""),
    label: String(w.title ?? w.name ?? ""),
  }));

  // ── Mutation ──────────────────────────────────────────────────────────────

  const mutation = useApiPost("package/addupdate-packages");

  // ── Form ──────────────────────────────────────────────────────────────────

  const form = useForm<FormValues>({
    resolver: zodResolver(schema) as any,
    defaultValues: {
      name: "",
      priority: "",
      purchase_type: "",
      package_type: "",
      price: "",
      sessions: "",
      subscription_period: "",
      description: "",
      webinar_ids: [],
      image: [],
      status: true,
    },
  });

  // ── Hydrate edit record ───────────────────────────────────────────────────

  useEffect(() => {
    if (record) {
      form.reset({
        name: record.name ?? "",
        priority: record.priority != null ? String(record.priority) : "",
        purchase_type: record.purchase_type ?? "",
        package_type: record.package_type ?? "",
        price: record.price != null ? String(record.price) : "",
        sessions: record.sessions != null ? String(record.sessions) : "",
        subscription_period: record.subscription_period ?? "",
        description: record.description ?? "",
        // PM-02: hydrate webinar_ids
        webinar_ids: Array.isArray(record.webinar_ids)
          ? record.webinar_ids.map(String)
          : [],
        // PM-02: hydrate image metadata
        image: Array.isArray(record.image)
          ? record.image
          : record.image
          ? [record.image]
          : [],
        status: record.status === 1 || record.status === true,
      });
    }
  }, [record, form]);

  // ── PM-03: Clear stale branch values when purchase type changes ───────────

  const purchaseType = form.watch("purchase_type");
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // Clear session when switching away from multiple
    if (purchaseType !== "multiple") {
      form.setValue("sessions", "", { shouldValidate: false });
    }
    // Clear subscription_period when switching away from subscription
    if (purchaseType !== "subscription") {
      form.setValue("subscription_period", "", { shouldValidate: false });
    }
  }, [purchaseType, form]);

  const isMultiple = purchaseType === "multiple";
  const isSubscription = purchaseType === "subscription";

  // ── Submit ────────────────────────────────────────────────────────────────

  async function onSubmit(values: FormValues) {
    try {
      const userId = (session?.user as unknown as Record<string, unknown>)?._id as string;
      const body: Record<string, unknown> = {
        name: values.name,
        priority: parseFloat(values.priority) || 0,
        purchase_type: values.purchase_type,
        package_type: values.package_type || undefined,
        price: parseFloat(values.price),
        description: values.description,
        webinar_ids: values.webinar_ids ?? [],
        image: values.image ?? [],
        status: values.status ? 1 : 0,
        added_by: userId,
        for_customer: false,
      };

      // PM-03: include only the relevant branch field
      if (isMultiple && values.sessions) {
        body.sessions = parseFloat(values.sessions);
      }
      if (isSubscription && values.subscription_period) {
        body.subscription_period = values.subscription_period;
      }
      if (isEdit) body._id = id;

      await mutation.mutateAsync(body as any);
      toast.success(isEdit ? "Package updated successfully" : "Package created successfully");
      router.push("/admin/packages");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Operation failed");
    }
  }

  // ── Loading skeleton ──────────────────────────────────────────────────────

  if (isEdit && isFetching) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="shrink-0" onClick={() => router.back()}>
          <ArrowLeft className="size-4" />
        </Button>
        <div
          className="h-6 w-1 rounded-full shrink-0"
          style={{ background: "linear-gradient(to bottom, #ffa800, #ff7e00)" }}
        />
        <div>
          <h1 className="text-xl font-semibold text-foreground">
            {isEdit ? "Edit Package" : "Add Package"}
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {isEdit ? "Update package details" : "Create a new package"}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader
          className="border-b"
          style={{ borderColor: "hsl(240 20% 20%)" }}
        >
          <CardTitle className="flex items-center gap-2 text-base">
            <span className="h-4 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
            Package Details
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <TextField
                  control={form.control}
                  name="name"
                  label="Package Name"
                  placeholder="Enter package name"
                />
                <TextField
                  control={form.control}
                  name="priority"
                  label="Priority"
                  type="number"
                  placeholder="e.g. 1"
                />

                <SelectField
                  control={form.control}
                  name="purchase_type"
                  label="Purchase Type"
                  options={[
                    { value: "single", label: "Single" },
                    { value: "multiple", label: "Multiple" },
                    { value: "subscription", label: "Subscription" },
                  ]}
                  placeholder="Select purchase type"
                />

                <SelectField
                  control={form.control}
                  name="package_type"
                  label="Package Type"
                  options={[
                    { value: "astrology_package", label: "Astrology Package" },
                    { value: "tarot_package", label: "Tarot Package" },
                    { value: "combo_package", label: "Combo Package" },
                    { value: "customer_package", label: "Customer Package" },
                  ]}
                  placeholder="Select package type"
                />

                {/* PM-02: price required */}
                <TextField
                  control={form.control}
                  name="price"
                  label="Price ($)"
                  type="number"
                  placeholder="0.00"
                />

                {/* PM-03: sessions — only shown and validated when purchase_type is "multiple" */}
                {isMultiple && (
                  <TextField
                    control={form.control}
                    name="sessions"
                    label="Number of Sessions"
                    type="number"
                    placeholder="e.g. 5"
                  />
                )}

                {/* PM-03: subscription_period — only shown and validated when purchase_type is "subscription" */}
                {isSubscription && (
                  <SelectField
                    control={form.control}
                    name="subscription_period"
                    label="Subscription Period"
                    options={[
                      { value: "daily", label: "Daily" },
                      { value: "monthly", label: "Monthly" },
                      { value: "quarterly", label: "Quarterly" },
                      { value: "yearly", label: "Yearly" },
                    ]}
                    placeholder="Select period"
                  />
                )}

                {/* PM-02: webinar multi-select */}
                <div className="md:col-span-2">
                  <MultiSelectField
                    control={form.control}
                    name="webinar_ids"
                    label="Linked Webinars"
                    options={webinarOptions}
                    placeholder="Select webinars..."
                  />
                </div>

                {/* PM-02: description required */}
                <div className="md:col-span-2">
                  <TextareaField
                    control={form.control}
                    name="description"
                    label="Description"
                    placeholder="Package description..."
                  />
                </div>

                {/* PM-02: package image upload via S3 */}
                <div className="md:col-span-2">
                  <S3UploadField
                    control={form.control}
                    name="image"
                    label="Package Image"
                    bucket="all-frontend-assets"
                    path="divine-infinity-being/package-image/"
                    accept="image/*"
                  />
                </div>

                <div className="md:col-span-2">
                  <SwitchField
                    control={form.control}
                    name="status"
                    label="Active"
                    description="Make this package available"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={mutation.isPending}
                  className="inline-flex items-center gap-2 rounded-md px-5 py-2 text-sm font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed btn-orange"
                >
                  {mutation.isPending ? "Saving..." : isEdit ? "Update" : "Create"}
                </button>
                <Button type="button" variant="outline" onClick={() => router.back()}>
                  Cancel
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
